import moment from 'moment-timezone';
// This file is only handling danish bank holidays - therefore the timezone is not retrieved from the tenant
export const COPENHAGEN_TZ = 'Europe/Copenhagen';
// Hour of day (Copenhagen local time) to schedule recurring debits at.
// Most Danish banks have credited overnight salary by ~05:00; 06:00 is
// late enough to capture the late-crediting banks while still being well
// before the day's other automated debits clear the account.
export const DEFAULT_PAYMENT_HOUR_LOCAL = 6;
// Anonymous Gregorian algorithm (Meeus / Butcher) for Easter Sunday.
const easterSunday = (year) => {
    const a = year % 19;
    const b = Math.floor(year / 100);
    const c = year % 100;
    const d = Math.floor(b / 4);
    const e = b % 4;
    const f = Math.floor((b + 8) / 25);
    const g = Math.floor((b - f + 1) / 3);
    const h = (19 * a + b - d - g + 15) % 30;
    const i = Math.floor(c / 4);
    const k = c % 4;
    const l = (32 + 2 * e + 2 * i - h - k) % 7;
    const m = Math.floor((a + 11 * h + 22 * l) / 451);
    const month = Math.floor((h + l - 7 * m + 114) / 31);
    const day = ((h + l - 7 * m + 114) % 31) + 1;
    return { month, day };
};
// Easter-relative Danish bank holidays (offsets in days from Easter Sunday):
// -3 Skærtorsdag, -2 Langfredag, +1 2. Påskedag, +39 Kr. Himmelfartsdag,
// +50 2. Pinsedag.
const EASTER_HOLIDAY_OFFSETS = [-3, -2, 1, 39, 50];
// Fixed-date Danish bank-closed days.
// Banks are also typically closed on 24/12 and 31/12 even though they are
// not statutory helligdage; treat them as non-bank days for safety.
const FIXED_BANK_CLOSED_MMDD = new Set([
    '01-01', // Nytårsdag
    '06-05', // Grundlovsdag (banks closed)
    '12-24', // Juleaftensdag (banks closed)
    '12-25', // Juledag
    '12-26', // 2. juledag
    '12-31', // Nytårsaftensdag (banks closed)
]);
const isDanishHoliday = (date) => {
    const local = date.clone().tz(COPENHAGEN_TZ);
    if (FIXED_BANK_CLOSED_MMDD.has(local.format('MM-DD'))) {
        return true;
    }
    const year = local.year();
    const easter = easterSunday(year);
    const easterMoment = moment
        .tz(`${year}-${String(easter.month).padStart(2, '0')}-${String(easter.day).padStart(2, '0')}`, COPENHAGEN_TZ)
        .startOf('day');
    for (const offset of EASTER_HOLIDAY_OFFSETS) {
        const holiday = easterMoment.clone().add(offset, 'days');
        if (local.isSame(holiday, 'day')) {
            return true;
        }
    }
    return false;
};
export const isDanishBankDay = (date) => {
    const local = date.clone().tz(COPENHAGEN_TZ);
    const day = local.day();
    if (day === 0 || day === 6) {
        return false;
    }
    return !isDanishHoliday(local);
};
const setPaymentTime = (date) => date.hour(DEFAULT_PAYMENT_HOUR_LOCAL).minute(0).second(0).millisecond(0);
// Snap to the last Danish bank day in the same calendar month (Copenhagen
// time) of `input`. The result is fixed at 06:00 Copenhagen so card-based
// debits hit just after typical salary credit (~02:00–06:00) but before
// the day's other scheduled withdrawals.
export const lastDanishBankDayOfMonth = (input) => {
    const candidate = input
        .clone()
        .tz(COPENHAGEN_TZ)
        .endOf('month')
        .startOf('day');
    while (!isDanishBankDay(candidate)) {
        candidate.subtract(1, 'day');
    }
    return setPaymentTime(candidate);
};
export const defaultLastBankDayStart = (now) => {
    const reference = now.clone().tz(COPENHAGEN_TZ);
    const base = reference.date() < 20 ? reference : reference.add(1, 'month');
    return lastDanishBankDayOfMonth(base);
};
// Snap backwards to the nearest Danish bank day on or before `input`,
// fixed at 06:00 Copenhagen. Useful for sub-monthly intervals (weekly,
// bi-weekly) where "last bank day of month" doesn't apply.
export const nearestPriorDanishBankDay = (input) => {
    const candidate = input.clone().tz(COPENHAGEN_TZ).startOf('day');
    while (!isDanishBankDay(candidate)) {
        candidate.subtract(1, 'day');
    }
    return setPaymentTime(candidate);
};
//# sourceMappingURL=danish-bank-days.js.map